import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import { PageLayout } from '../components/common/PageLayout'
import { Section, Button, GameCard } from '../components/common/DesignComponents'
import { FilterChips, Alert } from '../components/common/LayoutPatterns'

interface ShopItem {
  id: string
  name: string
  description: string
  icon: string
  rarity: 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary'
  cost: number
  currencyType: 'SEASONAL_TOKEN' | 'EVENT_TOKEN' | 'COINS'
  seasonalOnly: boolean
  quantity: number
}

interface CurrencyBalance {
  childId: string
  coins: number
  seasonalTokens: number
  eventTokens: Record<string, number>
}

export const SeasonalShopPage: React.FC = () => {
  const { childId } = useParams<{ childId: string }>()
  const [shopItems, setShopItems] = useState<ShopItem[]>([])
  const [balance, setBalance] = useState<CurrencyBalance | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [purchasing, setPurchasing] = useState<string | null>(null)
  const [quantities, setQuantities] = useState<Record<string, number>>({})
  const [filter, setFilter] = useState<'all' | 'seasonal' | 'event'>('all')

  useEffect(() => {
    fetchShopData()
  }, [childId])

  const fetchShopData = async () => {
    if (!childId) return

    try {
      setLoading(true)
      setError(null)

      const [shopRes, balanceRes] = await Promise.all([
        axios.get('/api/rewards/shop'),
        axios.get(`/api/rewards/children/${childId}/balance`),
      ])

      const items = shopRes.data.data
      setShopItems(items)

      // Initialize quantities to 1
      const initialQuantities: Record<string, number> = {}
      items.forEach((item: ShopItem) => {
        initialQuantities[item.id] = 1
      })
      setQuantities(initialQuantities)

      setBalance(balanceRes.data.data)
    } catch (err) {
      console.error('Failed to fetch shop data:', err)
      setError('Failed to load shop. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handlePurchase = async (item: ShopItem) => {
    if (!childId || !balance) return

    const quantity = quantities[item.id] || 1
    const totalCost = item.cost * quantity

    // Check if child has enough currency
    let hasEnough = false
    if (item.currencyType === 'SEASONAL_TOKEN') {
      hasEnough = balance.seasonalTokens >= totalCost
    } else if (item.currencyType === 'EVENT_TOKEN') {
      hasEnough = (balance.eventTokens[item.id] || 0) >= totalCost
    } else if (item.currencyType === 'COINS') {
      hasEnough = balance.coins >= totalCost
    }

    if (!hasEnough) {
      setError(`Not enough ${item.currencyType.toLowerCase()} for this purchase`)
      return
    }

    try {
      setPurchasing(item.id)
      await axios.post('/api/rewards/purchase', {
        childId,
        rewardId: item.id,
        quantity,
      })

      // Refresh balance
      const res = await axios.get(`/api/rewards/children/${childId}/balance`)
      setBalance(res.data.data)

      // Show success message
      setError(null)
    } catch (err) {
      console.error('Failed to purchase item:', err)
      setError('Purchase failed. Please try again.')
    } finally {
      setPurchasing(null)
    }
  }

  const getCurrencyBalance = (currencyType: string): number => {
    if (!balance) return 0
    if (currencyType === 'SEASONAL_TOKEN') return balance.seasonalTokens
    if (currencyType === 'COINS') return balance.coins
    return balance.eventTokens[currencyType] || 0
  }

  const getCurrencyIcon = (type: string): React.ReactNode => {
    switch (type) {
      case 'COINS':
        return '💰'
      case 'SEASONAL_TOKEN':
        return '🎀'
      case 'EVENT_TOKEN':
        return '🎊'
      default:
        return '✨'
    }
  }

  const filteredItems = shopItems.filter((item) => {
    if (filter === 'seasonal') return item.seasonalOnly
    if (filter === 'event') return item.currencyType === 'EVENT_TOKEN'
    return true
  })

  if (loading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin text-5xl mb-4">🛍️</div>
            <p className="text-purple-600 font-semibold">Loading shop...</p>
          </div>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout>
      <Section>
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600 mb-2">
            🛍️ Seasonal Shop
          </h1>
          <p className="text-gray-600">Purchase seasonal rewards and special items</p>
        </div>

        {error && (
          <Alert
            type="error"
            title="Purchase Error"
            message={error}
            icon="❌"
            className="mb-6"
          />
        )}

        {/* Currency Balance */}
        {balance && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <GameCard
              title="💰 Coins"
              icon="💰"
              badges={[`${balance.coins.toLocaleString()} coins`]}
              className="bg-gradient-to-br from-yellow-50 to-orange-50"
            >
              <p className="text-3xl font-bold text-yellow-600">
                {balance.coins.toLocaleString()}
              </p>
            </GameCard>

            <GameCard
              title="🎀 Seasonal Tokens"
              icon="🎀"
              badges={[`${balance.seasonalTokens} tokens`]}
              className="bg-gradient-to-br from-pink-50 to-rose-50"
            >
              <p className="text-3xl font-bold text-pink-600">
                {balance.seasonalTokens}
              </p>
            </GameCard>

            <GameCard
              title="🎊 Event Tokens"
              icon="🎊"
              badges={[`${Object.values(balance.eventTokens).reduce((a, b) => a + b, 0)} tokens`]}
              className="bg-gradient-to-br from-purple-50 to-indigo-50"
            >
              <p className="text-3xl font-bold text-purple-600">
                {Object.values(balance.eventTokens).reduce((a, b) => a + b, 0)}
              </p>
            </GameCard>
          </div>
        )}

        {/* Filters */}
        <div className="mb-6 flex gap-3 flex-wrap">
          <FilterChips
            chips={[
              { label: 'All Items', selected: filter === 'all', onClick: () => setFilter('all') },
              { label: 'Seasonal Only', selected: filter === 'seasonal', onClick: () => setFilter('seasonal') },
              { label: 'Event Items', selected: filter === 'event', onClick: () => setFilter('event') },
            ]}
          />
        </div>

        {/* Shop Grid */}
        {filteredItems.length === 0 ? (
          <div className="text-center py-12 bg-purple-50 rounded-xl border-2 border-dashed border-purple-200">
            <p className="text-3xl mb-3">🛍️</p>
            <p className="text-gray-600 text-lg">No items available in this category</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredItems.map((item) => {
              const quantity = quantities[item.id] || 1
              const totalCost = item.cost * quantity
              const canAfford = getCurrencyBalance(item.currencyType) >= totalCost
              const currencyEmoji = getCurrencyIcon(item.currencyType)

              return (
                <GameCard
                  key={item.id}
                  title={item.name}
                  icon={item.icon}
                  badges={[
                    item.seasonalOnly && '⭐ SEASONAL',
                    item.rarity.toUpperCase(),
                  ].filter(Boolean) as string[]}
                  className="bg-gradient-to-br from-purple-50 to-pink-50"
                >
                  <p className="text-gray-600 text-sm mb-4">{item.description}</p>

                  {/* Cost Display */}
                  <div className="bg-white rounded-lg p-3 mb-4 border border-purple-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{currencyEmoji}</span>
                        <span className="font-bold text-purple-600">
                          {item.cost.toLocaleString()}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">x{quantity}</span>
                    </div>
                    <div className="text-center text-sm text-purple-600 font-semibold mt-2">
                      Total: {currencyEmoji} {totalCost.toLocaleString()}
                    </div>
                  </div>

                  {/* Quantity Selector */}
                  <div className="flex items-center gap-2 mb-4 bg-white rounded-lg p-2 border border-purple-100">
                    <button
                      onClick={() =>
                        setQuantities({
                          ...quantities,
                          [item.id]: Math.max(1, (quantities[item.id] || 1) - 1),
                        })
                      }
                      className="p-1 hover:bg-purple-100 rounded transition text-purple-600"
                    >
                      −
                    </button>
                    <input
                      type="number"
                      min="1"
                      max="99"
                      value={quantity}
                      onChange={(e) =>
                        setQuantities({
                          ...quantities,
                          [item.id]: Math.max(
                            1,
                            Math.min(99, parseInt(e.target.value) || 1)
                          ),
                        })
                      }
                      className="flex-1 text-center bg-transparent border-none text-purple-600 font-bold"
                    />
                    <button
                      onClick={() =>
                        setQuantities({
                          ...quantities,
                          [item.id]: Math.min(99, (quantities[item.id] || 1) + 1),
                        })
                      }
                      className="p-1 hover:bg-purple-100 rounded transition text-purple-600"
                    >
                      +
                    </button>
                  </div>

                  {/* Purchase Button */}
                  <Button
                    onClick={() => handlePurchase(item)}
                    disabled={
                      purchasing === item.id ||
                      !canAfford ||
                      !balance
                    }
                    className={`w-full ${
                      canAfford && balance
                        ? 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600'
                        : 'bg-gray-300 cursor-not-allowed opacity-50'
                    }`}
                  >
                    {purchasing === item.id ? (
                      <span>⏳ Purchasing...</span>
                    ) : canAfford ? (
                      <span>🛒 Buy Now</span>
                    ) : (
                      <span>🔒 Not Enough</span>
                    )}
                  </Button>
                </GameCard>
              )
            })}
          </div>
        )}
      </Section>
    </PageLayout>
  )
}

export default SeasonalShopPage
